/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth, UserModel} from '../modules/auth'
import {App} from '../App'
import {ForgotPasswordPage} from '../modules/forgot-password/ForgotPasswordPage'
import AttendancePage from '../modules/attendance/AttendancePage'
import {SurveyPage} from '../modules/survey/SurveyPage'
import {Registration} from '../modules/auth/components/Registration'
import SebExamPage from '../modules/seb-exam/SebExamPage'
import ExamLoginPage from '../modules/exam-login/ExamLoginPage'
import isSafeExamBrowser from '../utils/seb'
import {getAuth} from '../modules/auth/core/AuthHelpers'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser, isOtpValid} = useAuth()

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route path='attendance/*' element={<AttendancePage />}></Route>
          <Route path='survey/*' element={<SurveyPage />}></Route>
          {authRoute(currentUser, isOtpValid)}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

const authRoute = (currentUser: UserModel | undefined, isOtpValid: boolean): JSX.Element => {
  const pathName = window.location.pathname

  if (pathName.includes('safe-exam-browser')) {
    if (isSafeExamBrowser()) {
      return (
        <>
          <Route path='/safe-exam-browser/*' element={<SebExamPage />} />
        </>
      )
    }
  }

  if (currentUser && pathName !== '/auth/forgot-password/reset') {
    const authMode = getAuth()?.authMode
    if (authMode === 'exam') {
      return (
        <>
          <Route path='/exam-login/*' element={<ExamLoginPage />} />
          <Route path='*' element={<Navigate to='/exam-login' />} />
        </>
      )
    }

    return (
      <>
        <Route path='/*' element={<PrivateRoutes />} />
        <Route index element={<Navigate to='/dashboard' />} />
      </>
    )
  }

  return (
    <>
      <Route path='auth/*' element={<AuthPage />} />
      <Route path='/register' element={<Registration />} />
      <Route path='/auth/forgot-password/*' element={<ForgotPasswordPage />} />
      <Route path='*' element={<Navigate to='/auth' />} />
    </>
  )
}

export {AppRoutes}
